'use client'

import { useCallback } from 'react'

// Components
import { useWalletValidator } from '@features/cpn.hooks/useWalletValidator'

import { MethodSelection } from './MethodSelection/MethodSelection'
import { ParameterInput } from './ParameterInput/ParameterInput'
import { SignatureValidation } from './SignatureValidation/SignatureValidation'
import { StepIndicator } from './StepIndicator/StepIndicator'
import { ValidationResults } from './ValidationResults/ValidationResults'
import { DEFAULT_CLASSES } from './constants'

/**
 * Main wallet compatibility validator component
 * Coordinates the multi-step wallet testing wizard flow.
 */
export const WalletCompatibilityValidator: React.FC = () => {
  const {
    // State
    currentStep,
    selectedMethod,
    generatedPayload,
    validationResult,
    isValidating,
    error,

    // Handlers
    handleMethodSelection,
    handlePayloadGeneration,
    handleSignatureValidation,
    handleBackToMethod,
    handleBackToParameters,
    handleBackToSignature,
    resetFlow,
  } = useWalletValidator()

  // Wrapper to handle async signature validation
  const handleSignatureSubmit = useCallback(
    (data: { signature: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleSignatureValidation(data)
    },
    [handleSignatureValidation],
  )

  return (
    <div className={DEFAULT_CLASSES.container}>
      {/* Header */}
      <div className={DEFAULT_CLASSES.header}>
        <h3 className="text-neutral-strong type-h-title-md">
          Wallet Compatibility Validator
        </h3>
        <p className="mt-1 text-neutral-subtle type-body-sm">
          Test your wallet&apos;s compatibility with Circle Payments Network
          (CPN)
        </p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} />

      {/* Content */}
      <div className={DEFAULT_CLASSES.content}>
        {/* Step 1: Method Selection */}
        {currentStep === 'method' && (
          <MethodSelection onMethodSelect={handleMethodSelection} />
        )}

        {/* Step 2: Parameters */}
        {currentStep === 'parameters' && selectedMethod && (
          <ParameterInput
            error={error}
            onBack={handleBackToMethod}
            onParametersSubmit={handlePayloadGeneration}
            selectedMethod={selectedMethod}
          />
        )}

        {/* Step 3: Signature Input */}
        {currentStep === 'signature' && generatedPayload && (
          <SignatureValidation
            generatedPayload={generatedPayload}
            isValidating={isValidating}
            onBack={handleBackToParameters}
            onSignatureSubmit={handleSignatureSubmit}
          />
        )}

        {/* Step 4: Results */}
        {currentStep === 'results' && validationResult && (
          <ValidationResults
            onRestart={resetFlow}
            onRetry={handleBackToSignature}
            selectedMethod={selectedMethod}
            validationResult={validationResult}
          />
        )}
      </div>
    </div>
  )
}
